import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import styles from './Agendamentos.module.css';
import LoadingSpinner from '../components/LoadingSpinner';

const Agendamentos = () => {
  const navigate = useNavigate();
  const [agendamentos, setAgendamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({
    data: new Date().toISOString().split('T')[0],
    status: '' // Vazio = todos os status
  });
  const [error, setError] = useState('');

  useEffect(() => {
    carregarAgendamentos();
  }, [filtros]);

  const carregarAgendamentos = async () => {
    try {
      setLoading(true);
      setError('');
      
      const params = new URLSearchParams();
      if (filtros.data) params.append('data', filtros.data);
      if (filtros.status) params.append('status', filtros.status);

      const response = await axios.get(`/agendamentos/meus-agendamentos?${params}`);
      setAgendamentos(response.data);
    } catch (err) {
      console.error('Erro ao carregar agendamentos:', err);
      setError(err.response?.data?.message || 'Erro ao carregar agendamentos');
    } finally {
      setLoading(false);
    }
  };

  const iniciarAtendimento = async (consultaId) => {
    try {
      setLoading(true);
      const response = await axios.post(`/agendamentos/${consultaId}/iniciar-atendimento`);
      
      // Navegar para a tela de atendimento com os dados do prontuário
      // Backend retorna: { success: true, data: { consulta, paciente, prontuario, ... } }
      navigate(`/atendimento/${consultaId}`, { 
        state: { 
          prontuarioData: response.data.data || response.data
        } 
      });
    } catch (err) {
      console.error('Erro ao iniciar atendimento:', err);
      setError(err.response?.data?.message || 'Erro ao iniciar atendimento');
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'agendado': { class: 'badgeAgendada', text: 'Agendado' },
      'em_andamento': { class: 'badgeAtendimento', text: 'Em Andamento' },
      'concluido': { class: 'badgeConcluida', text: 'Concluído' },
      'cancelado': { class: 'badgeCancelada', text: 'Cancelado' }
    };
    const badge = badges[status] || { class: 'badgeDefault', text: status };
    return <span className={`${styles.badge} ${styles[badge.class]}`}>{badge.text}</span>;
  };

  const formatarData = (dataStr) => {
    const data = new Date(dataStr);
    return data.toLocaleDateString('pt-BR');
  };

  const formatarHora = (horaStr) => {
    return horaStr?.substring(0, 5) || '';
  };

  if (loading && agendamentos.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Meus Agendamentos</h1>
        <button 
          className={styles.btnRefresh}
          onClick={carregarAgendamentos}
          disabled={loading}
        >
          🔄 Atualizar
        </button>
      </div>

      {/* Filtros */}
      <div className={styles.filtros}>
        <div className={styles.filtroGroup}>
          <label>Data:</label>
          <input
            type="date"
            value={filtros.data}
            onChange={(e) => setFiltros({ ...filtros, data: e.target.value })}
            className={styles.input}
          />
        </div>
        
        <div className={styles.filtroGroup}>
          <label>Status:</label>
          <select
            value={filtros.status}
            onChange={(e) => setFiltros({ ...filtros, status: e.target.value })}
            className={styles.select}
          >
            <option value="">Todos</option>
            <option value="agendado">Agendado</option>
            <option value="em_andamento">Em Andamento</option>
            <option value="concluido">Concluído</option>
            <option value="cancelado">Cancelado</option>
          </select>
        </div>
      </div>

      {error && (
        <div className={styles.error}>
          ⚠️ {error}
        </div>
      )}

      {/* Lista de Agendamentos */}
      <div className={styles.agendamentosList}>
        {agendamentos.length === 0 ? (
          <div className={styles.empty}>
            📅 Nenhum agendamento encontrado para os filtros selecionados
          </div>
        ) : (
          agendamentos.map((agendamento) => (
            <div key={agendamento.consulta_id} className={styles.agendamentoCard}>
              <div className={styles.cardHeader}>
                <div className={styles.horario}>
                  <span className={styles.hora}>
                    {formatarHora(agendamento.horario)}
                  </span>
                  <span className={styles.data}>
                    {formatarData(agendamento.data_consulta)}
                  </span>
                </div>
                {getStatusBadge(agendamento.status)}
              </div>

              <div className={styles.cardBody}>
                <div className={styles.pacienteInfo}>
                  <h3>👤 {agendamento.paciente_nome}</h3>
                  {agendamento.paciente_cpf && (
                    <p className={styles.cpf}>CPF: {agendamento.paciente_cpf}</p>
                  )}
                  {agendamento.paciente_data_nascimento && (
                    <p className={styles.idade}>
                      Nascimento: {formatarData(agendamento.paciente_data_nascimento)}
                    </p>
                  )}
                </div>

                {agendamento.tipo_consulta && (
                  <div className={styles.tipoConsulta}>
                    <strong>Tipo:</strong> {agendamento.tipo_consulta}
                  </div>
                )}

                {agendamento.tem_prontuario && (
                  <div className={styles.prontuarioInfo}>
                    <span className={styles.iconeProntuario}>📋</span>
                    <span>Prontuário: {agendamento.numero_prontuario}</span>
                    {agendamento.total_evolucoes > 0 && (
                      <span className={styles.evolucoes}>
                        ({agendamento.total_evolucoes} {agendamento.total_evolucoes === 1 ? 'evolução' : 'evoluções'})
                      </span>
                    )}
                  </div>
                )}

                {agendamento.observacoes && (
                  <div className={styles.observacoes}>
                    <strong>Observações:</strong> {agendamento.observacoes}
                  </div>
                )}
              </div>

              <div className={styles.cardFooter}>
                {agendamento.status === 'agendado' && (
                  <button
                    className={styles.btnAtender}
                    onClick={() => iniciarAtendimento(agendamento.consulta_id)}
                    disabled={loading}
                  >
                    🩺 Iniciar Atendimento
                  </button>
                )}
                
                {agendamento.status === 'em_andamento' && (
                  <button
                    className={styles.btnContinuar}
                    onClick={() => navigate(`/atendimento/${agendamento.consulta_id}`)}
                  >
                    ▶️ Continuar Atendimento
                  </button>
                )}

                {agendamento.status === 'concluido' && agendamento.tem_prontuario && (
                  <button
                    className={styles.btnVisualizar}
                    onClick={() => navigate(`/atendimento/${agendamento.consulta_id}`, { 
                      state: { 
                        modoVisualizacao: true 
                      } 
                    })}
                  >
                    👁️ Visualizar Prontuário
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Agendamentos;
